/**
 * MetaInsightsSections — análisis detallado que solo existe cuando la fuente es
 * la Marketing API de Meta, y que el esquema mensual de `records` no puede
 * representar: audiencia (edad, género, estados, países, dispositivo),
 * ubicaciones, anuncios con su creativo, embudo de video y acciones.
 *
 * Se monta DEBAJO del tablero de Campañas cuando source="meta" y respeta el
 * mismo periodo que el filtro de arriba. Diseño:
 *
 *  - Una sola tarjeta con PESTAÑAS en vez de nueve tarjetas apiladas: cada
 *    pestaña responde una pregunta (¿a quién le llegó?, ¿dónde se vio?, ¿qué
 *    anuncio funcionó?, ¿vieron el video?, ¿qué hicieron?). Una pestaña sin
 *    datos no se ofrece.
 *  - "Analizar": toda la cuenta o UNA campaña. Con una campaña, todos los
 *    bloques se vuelven a pedir filtrados a ella.
 *  - Un bloque sin datos NO se pinta: una gráfica en cero se lee como "no hubo
 *    resultados". Lo que la API niegue se lista una vez, plegado, al final.
 *  - El alcance NO se suma mes a mes: viene de su propio request sin desglose
 *    temporal (sumar alcance da alcance acumulado, no personas únicas).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  FaExclamationTriangle, FaSync, FaVenusMars, FaUsers, FaMapMarkedAlt, FaImages, FaPlayCircle, FaSearchPlus,
} from 'react-icons/fa';
import { compact, dec, numFmt, pct, resultLine } from './metaAdsShared';
import { CreativeGrid } from './MetaCreatives';
import './MetaInsightsSections.css';
import './MetaAds.css';

const NAVY = '#073C5C';
const ORANGE = '#F68D2E';
const PALETTE = [NAVY, ORANGE, '#3b82f6', '#10b981', '#8b5cf6', '#ec4899',
  '#ef4444', '#14b8a6', '#f59e0b', '#64748b'];

const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'unknown'];
const GENDER_LABEL = { male: 'Hombres', female: 'Mujeres', unknown: 'Sin especificar' };
const GENDER_COLOR = { male: '#3b82f6', female: '#ec4899', unknown: '#94a3b8' };

const PLATFORM_LABEL = {
  facebook: 'Facebook', instagram: 'Instagram', messenger: 'Messenger',
  audience_network: 'Audience Network', threads: 'Threads', whatsapp: 'WhatsApp', unknown: 'Sin identificar',
};
// Ubicaciones de Meta. Las que no esten aqui se muestran con los guiones bajos
// convertidos en espacios: es preferible un nombre feo a esconder inventario.
const POSITION_LABEL = {
  feed: 'Feed', instagram_stories: 'Historias', instagram_reels: 'Reels',
  facebook_reels: 'Reels', story: 'Historias', instream_video: 'Video in-stream',
  video_feeds: 'Feed de video', marketplace: 'Marketplace', search: 'Búsqueda',
  right_hand_column: 'Columna derecha', explore: 'Explorar', explore_home: 'Explorar (inicio)',
  profile_feed: 'Feed de perfil', messenger_inbox: 'Bandeja', an_classic: 'Clásico',
  facebook_stories: 'Historias', instagram_explore: 'Explorar', facebook_reels_overlay: 'Reels (superpuesto)',
  instagram_profile_reels: 'Reels del perfil', instagram_search: 'Búsqueda',
  status: 'Estados', threads_feed: 'Feed de Threads', notification: 'Notificaciones',
};
const DEVICE_LABEL = {
  android_smartphone: 'Android', iphone: 'iPhone', ipad: 'iPad',
  android_tablet: 'Tablet Android', desktop: 'Escritorio', other: 'Otro', ipod: 'iPod',
};
const pretty = (map, v) => map[v] || (v || '').replace(/_/g, ' ') || '—';

let regionNames = null;
try { regionNames = new Intl.DisplayNames(['es'], { type: 'region' }); } catch (_) { /* navegador viejo */ }
const countryName = (code) => {
  if (!code || String(code).toLowerCase() === 'unknown') return 'Sin país';
  try { return regionNames ? regionNames.of(code) : code; } catch (_) { return code; }
};

const TABS = [
  { id: 'audience', label: 'Audiencia', icon: <FaUsers /> },
  { id: 'placement', label: 'Ubicaciones', icon: <FaMapMarkedAlt /> },
  { id: 'ads', label: 'Anuncios', icon: <FaImages /> },
  { id: 'video', label: 'Video y acciones', icon: <FaPlayCircle /> },
];

export default function MetaInsightsSections({
  data, loading, error, money, campaigns = [], focus, onFocus, sectionRef,
}) {
  const bd = data?.breakdowns || {};
  const totals = data?.totals || {};
  const ageGender = bd.ageGender || [];
  const placements = bd.platform || [];
  // Menos de un peso se muestra como $0 y solo agrega una fila de ruido.
  const withSpend = (rows) => (rows || []).filter((r) => (r.spend || 0) >= 1);
  const countries = withSpend(bd.country);
  const regions = withSpend(bd.region);
  const devices = bd.device || [];
  const platformMonthly = data?.platformMonthly || [];
  const creatives = data?.creatives || [];
  const actions = data?.actionCatalog || [];
  const funnel = totals.videoFunnel;

  const available = {
    audience: !!(ageGender.length || countries.length || regions.length || devices.length),
    placement: !!(placements.length || platformMonthly.length > 1),
    ads: !!creatives.length,
    video: !!(funnel || actions.length),
  };
  const tabs = TABS.filter((t) => available[t.id]);
  const [tab, setTab] = useState('audience');
  const activeTab = available[tab] ? tab : tabs[0]?.id;
  useEffect(() => { if (activeTab && activeTab !== tab) setTab(activeTab); }, [activeTab, tab]);

  const focusSelect = campaigns.length > 1 && onFocus && (
    <label className="mad-focus">
      <FaSearchPlus />
      <span>Analizar</span>
      <select value={focus?.id || ''} onChange={(e) => {
        const c = campaigns.find((x) => x.id === e.target.value);
        onFocus(c || null);
      }}>
        <option value="">Toda la cuenta</option>
        {campaigns.map((c) => <option key={c.id} value={c.id} title={c.name}>{c.label || c.name}</option>)}
      </select>
    </label>
  );

  const head = (
    <div className="cmp-card-head mad-analysis-head">
      <span className="cmp-card-title">Análisis detallado de Meta{focus ? ` · ${focus.name}` : ''}</span>
      <div className="mad-head-tools">
        {loading && data && <span className="mad-muted"><FaSync className="cmp-spin" /> Actualizando…</span>}
        {focusSelect}
      </div>
    </div>
  );

  if (!data) {
    return (
      <section className="cmp-card mis-card" ref={sectionRef}>
        {head}
        {loading
          ? <div className="mis-loading"><FaSync className="cmp-spin" /> Consultando los desgloses de Meta…</div>
          : error
            ? <div className="mis-warn"><FaExclamationTriangle /><span>No se pudieron cargar los desgloses de Meta: {error}</span></div>
            : null}
      </section>
    );
  }

  const res = resultLine(totals.result, money);
  const warnings = data.warnings || [];

  return (
    <section className="cmp-card mis-card" ref={sectionRef}>
      {head}
      <div className="cmp-card-body">
        {/* ── Entrega real: lo que el esquema mensual no puede sumar ─────────── */}
        {(totals.reach != null || totals.frequency != null) && (
          <div className="mis-stats mad-delivery">
            {res && <Stat label={res.label.replace(/^\w/, (c) => c.toUpperCase())} value={res.value} sub={res.cost || 'resultado de la campaña'} accent />}
            <Stat label="Personas alcanzadas" value={compact(totals.reach)} sub="únicas, sin duplicar entre meses" />
            <Stat label="Frecuencia" value={dec(totals.frequency)} sub="veces que vio el anuncio cada persona" />
            <Stat label="CTR de salida" value={pct(totals.outboundCtr)} sub={`CTR total ${pct(totals.ctr)}`}
              hint="El CTR total cuenta clics en cualquier parte del anuncio, incluido “ver más”. El de salida solo los que llevaron al sitio." />
            <Stat label="Clics de salida" value={compact(totals.outboundClicks)} sub={`de ${compact(totals.clicks)} clics totales`} />
            {totals.roas != null
              ? <Stat label="Retorno (ROAS)" value={`${dec(totals.roas)}x`} sub={`${money(totals.purchaseValue)} en ventas atribuidas`} accent />
              : <Stat label="Costo por persona" value={totals.cpp != null ? money.unit(totals.cpp) : '—'} sub="alcanzada (CPP)" />}
          </div>
        )}

        {tabs.length > 0 && (
          <div className="mad-tabs" role="tablist">
            {tabs.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={activeTab === t.id}
                className={activeTab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        )}

        {tabs.length === 0 && !warnings.length && (
          <div className="cmp-empty">
            Meta no devolvió desgloses para este periodo. Suele significar que no hubo entrega de anuncios en el rango seleccionado.
          </div>
        )}

        {activeTab === 'audience' && (
          <div className="mad-tab-grid">
            {!!ageGender.length && (
              <Block title="Edad y género de quienes vieron los anuncios" wide
                hint="Meta eliminó la demografía de seguidores de la Página en la versión 21 de su API. Por la vía de pauta sí está: esto es a quién le llegó la inversión.">
                <Demographics rows={ageGender} money={money} />
              </Block>
            )}
            {!!devices.length && (
              <Block title="Dispositivo">
                <Donut items={aggregate(devices, (r) => pretty(DEVICE_LABEL, r.dims?.impression_device))} money={money} vertical />
              </Block>
            )}
            {regions.length > 1 && (
              <Block title="Estados con mayor inversión" wide={countries.length <= 1}>
                <GeoTable rows={regions.slice(0, 12)} nameOf={(r) => r.dims?.region || 'Sin estado'} money={money} />
              </Block>
            )}
            {countries.length > 1 && (
              <Block title="Países">
                <GeoTable rows={countries.slice(0, 12)} nameOf={(r) => countryName(r.dims?.country)} money={money} />
              </Block>
            )}
          </div>
        )}

        {activeTab === 'placement' && (
          <div className="mad-tab-grid">
            {!!placements.length && (
              <Block title="Inversión por red" hint="El tablero de arriba agrupa todo como “Meta”. Aquí se separa por red y por dónde se mostró el anuncio.">
                <Donut items={aggregate(placements, (r) => pretty(PLATFORM_LABEL, r.dims?.publisher_platform))} money={money} vertical />
              </Block>
            )}
            {!!placements.length && (
              <Block title="Rendimiento por ubicación" wide>
                <PlacementTable rows={placements} money={money} />
              </Block>
            )}
            {platformMonthly.length > 1 && (
              <Block title="Mix de red mes a mes" full>
                <PlatformTrend rows={platformMonthly} money={money} period={data.period} />
              </Block>
            )}
          </div>
        )}

        {activeTab === 'ads' && (
          <CreativeGrid creatives={creatives} money={money} total={data.totalAds} showCampaign={!focus} />
        )}

        {activeTab === 'video' && (
          <div className="mad-tab-grid">
            {funnel && (
              <Block title="Embudo de video" wide={!actions.length}
                hint="Distingue “vieron el anuncio” de “lo vieron completo”: un video con muchas reproducciones y nula retención se ve igual de bien que uno que funcionó.">
                <VideoFunnel totals={totals} />
              </Block>
            )}
            {!!actions.length && (
              <Block title="Acciones atribuidas" wide={!funnel}
                hint="Solo aparecen las acciones que esta cuenta registra. Si no hay compras es que no hay píxel de compra configurado, no que falten en la API.">
                <ActionsTable actions={actions} money={money} />
              </Block>
            )}
          </div>
        )}

        {/* Lo que Meta no entregó y los recortes de periodo: una vez, plegado. */}
        {(warnings.length > 0 || (data.notices || []).length > 0) && (
          <details className="mad-warnings">
            <summary><FaExclamationTriangle /> {warnings.length
              ? `Meta no entregó parte de los datos (${warnings.length})`
              : 'Notas sobre el periodo'}</summary>
            <ul>{[...(data.notices || []), ...warnings].map((w, i) => <li key={i}>{w}</li>)}</ul>
          </details>
        )}
      </div>
    </section>
  );
}

// ── Piezas ───────────────────────────────────────────────────────────────────

function Block({ title, hint, children, wide, full }) {
  return (
    <div className={`mad-block ${full ? 'full' : wide ? 'wide' : ''}`}>
      <h4 className="mad-block-title">{title}</h4>
      {hint && <p className="mis-hint mad-block-hint">{hint}</p>}
      {children}
    </div>
  );
}

function Stat({ label: l, value, sub, hint, accent }) {
  return (
    <div className={`mis-stat ${accent ? 'mad-accent' : ''}`} title={hint}>
      <span className="mis-stat-value">{value}</span>
      <span className="mis-stat-label">{l}</span>
      {sub && <span className="mis-stat-sub">{sub}</span>}
    </div>
  );
}

/** Agrupa filas de breakdown por una etiqueta y suma inversion e impresiones. */
function aggregate(rows, labelFn) {
  const acc = new Map();
  rows.forEach((r) => {
    const k = labelFn(r);
    const cur = acc.get(k) || { label: k, spend: 0, impressions: 0 };
    cur.spend += r.spend || 0;
    cur.impressions += r.impressions || 0;
    acc.set(k, cur);
  });
  return [...acc.values()].filter((x) => x.spend > 0).sort((a, b) => b.spend - a.spend).slice(0, 8);
}

function GeoTable({ rows, nameOf, money }) {
  const max = Math.max(...rows.map((r) => r.spend || 0), 1);
  return (
    <div className="cmp-table-scroll mad-table-wrap">
      <table className="cmp-table">
        <thead><tr><th>Lugar</th><th className="num">Inversión</th><th className="num">Alcance</th><th className="num">CTR</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <div className="mad-bar-cell">
                  <span>{nameOf(r)}</span>
                  <span className="mad-inline-bar"><span style={{ width: `${((r.spend || 0) / max) * 100}%` }} /></span>
                </div>
              </td>
              <td className="num">{money(r.spend)}</td>
              <td className="num">{compact(r.reach)}</td>
              <td className="num">{pct(r.ctr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlacementTable({ rows, money }) {
  const total = rows.reduce((a, r) => a + (r.spend || 0), 0) || 1;
  return (
    <div className="cmp-table-scroll mad-table-wrap">
      <table className="cmp-table">
        <thead>
          <tr><th>Red</th><th>Ubicación</th><th className="num">Inversión</th><th className="num">% del total</th><th className="num">CPM</th><th className="num">CTR</th></tr>
        </thead>
        <tbody>
          {rows.filter((r) => (r.spend || 0) > 0).slice(0, 12).map((r, i) => (
            <tr key={i}>
              <td>{pretty(PLATFORM_LABEL, r.dims?.publisher_platform)}</td>
              <td>{pretty(POSITION_LABEL, r.dims?.platform_position)}</td>
              <td className="num">{money(r.spend)}</td>
              <td className="num">{pct((r.spend || 0) / total, 1)}</td>
              <td className="num">{r.impressions ? money.unit(((r.spend || 0) / r.impressions) * 1000) : '—'}</td>
              <td className="num">{pct(r.ctr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionsTable({ actions, money }) {
  const hasValue = actions.some((a) => a.value);
  return (
    <div className="cmp-table-scroll mad-table-wrap">
      <table className="cmp-table">
        <thead>
          <tr><th>Acción</th><th className="num">Total</th><th className="num">Costo por acción</th>{hasValue && <th className="num">Valor</th>}</tr>
        </thead>
        <tbody>
          {actions.map((a) => (
            <tr key={a.key}>
              <td>{a.label}</td>
              <td className="num">{numFmt.format(Math.round(a.total || 0))}</td>
              <td className="num">{a.cost != null ? money.unit(a.cost) : '—'}</td>
              {hasValue && <td className="num">{a.value ? money(a.value) : '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Demographics({ rows, money }) {
  const [metric, setMetric] = useState('impressions');
  const { labels, datasets, top } = useMemo(() => {
    const ages = [];
    const byGender = {};
    rows.forEach((r) => {
      const age = String(r.dims?.age || 'unknown').toLowerCase();
      const gender = r.dims?.gender || 'unknown';
      if (!ages.includes(age)) ages.push(age);
      byGender[gender] = byGender[gender] || {};
      byGender[gender][age] = (byGender[gender][age] || 0) + (r[metric] || 0);
    });
    ages.sort((a, b) => AGE_ORDER.indexOf(a) - AGE_ORDER.indexOf(b));
    const ds = Object.entries(byGender).map(([g, vals]) => ({
      label: GENDER_LABEL[g] || g,
      data: ages.map((a) => vals[a] || 0),
      backgroundColor: GENDER_COLOR[g] || '#94a3b8',
      borderRadius: 5,
    }));
    const sorted = [...rows].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
    return { labels: ages.map((a) => (a === 'unknown' ? 'Sin dato' : a)), datasets: ds, top: sorted.slice(0, 3) };
  }, [rows, metric]);

  return (
    <>
      <div className="mad-seg small mad-seg-right">
        {[['impressions', 'Impresiones'], ['spend', 'Inversión'], ['clicks', 'Clics']].map(([id, l]) => (
          <button key={id} type="button" className={metric === id ? 'active' : ''} onClick={() => setMetric(id)}>{l}</button>
        ))}
      </div>
      <div className="cmp-chart-box tall">
        <Bar data={{ labels, datasets }} options={{
          responsive: true, maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'top', align: 'start', labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 } } },
            tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${metric === 'spend' ? money(c.parsed.y) : numFmt.format(c.parsed.y)}` } },
          },
          scales: { x: { grid: { display: false } }, y: { grid: { color: '#f1f5f9' }, ticks: { callback: (v) => compact(v) } } },
        }} />
      </div>
      <div className="mis-top">
        <FaVenusMars />
        {top.map((r, i) => (
          <span key={i}>
            {GENDER_LABEL[r.dims?.gender] || r.dims?.gender} {r.dims?.age}:{' '}
            <strong>{money(r.spend)}</strong> · CTR {pct(r.ctr)}
          </span>
        ))}
      </div>
    </>
  );
}

function PlatformTrend({ rows, money, period }) {
  const { labels, datasets } = useMemo(() => {
    const months = [];
    const byPlatform = {};
    rows.forEach((r) => {
      if (!months.includes(r.monthKey)) months.push(r.monthKey);
      const p = r.platform || 'unknown';
      byPlatform[p] = byPlatform[p] || {};
      byPlatform[p][r.monthKey] = (byPlatform[p][r.monthKey] || 0) + (r.spend || 0);
    });
    months.sort();
    const ds = Object.entries(byPlatform).map(([p, vals], i) => ({
      label: pretty(PLATFORM_LABEL, p),
      data: months.map((m) => vals[m] || 0),
      backgroundColor: PALETTE[i % PALETTE.length],
      borderRadius: 4,
    }));
    // El primer y el último mes pueden ser parciales (p. ej. "Últimos 30 días"):
    // se rotulan con sus días para no leer 6 días de agosto como "agosto".
    const since = period?.since || '';
    const until = period?.until || '';
    const monthLabel = (k) => {
      const base = new Date(`${k}-15T12:00:00`).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' }).replace('.', '');
      const [y, m] = k.split('-').map(Number);
      const last = new Date(y, m, 0).getDate();
      const d1 = since.startsWith(k) ? Number(since.slice(8, 10)) : 1;
      const d2 = until.startsWith(k) ? Number(until.slice(8, 10)) : last;
      if (d1 === 1 && d2 === last) return base;
      const mon = new Date(`${k}-15T12:00:00`).toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
      return d1 === d2 ? `${d1} ${mon}` : `${d1}–${d2} ${mon}`;
    };
    return { labels: months.map(monthLabel), datasets: ds };
  }, [rows, period]);

  return (
    <div className="cmp-chart-box">
      <Bar data={{ labels, datasets }} options={{
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 } } },
          tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y || 0)}` } },
        },
        scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, grid: { color: '#f1f5f9' }, ticks: { callback: (v) => compact(v) } } },
      }} />
    </div>
  );
}

function Donut({ items, money, vertical }) {
  const total = items.reduce((a, b) => a + b.spend, 0);
  return (
    <div className={`mad-donut ${vertical ? 'vertical' : ''}`}>
      <div className="mad-donut-chart">
        <Doughnut data={{
          labels: items.map((i) => i.label),
          datasets: [{ data: items.map((i) => i.spend), backgroundColor: items.map((_, i) => PALETTE[i % PALETTE.length]), borderWidth: 0 }],
        }} options={{
          responsive: true, maintainAspectRatio: false, cutout: '64%',
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.label}: ${money(c.parsed)}` } } },
        }} />
      </div>
      <ul className="cmp-legend">
        {items.map((i, idx) => (
          <li className="cmp-leg" key={idx}>
            <span className="cmp-leg-dot" style={{ background: PALETTE[idx % PALETTE.length] }} />
            <span className="cmp-leg-name">{i.label}</span>
            <span className="cmp-leg-val">{money(i.spend)}<span className="cmp-leg-pct">{total ? `${((i.spend / total) * 100).toFixed(0)}%` : '—'}</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VideoFunnel({ totals }) {
  const f = totals.videoFunnel || {};
  const base = totals.videoPlays || f[25] || 0;
  const steps = [['Reproducciones', base], ['25 %', f[25]], ['50 %', f[50]], ['75 %', f[75]], ['Completo', f[100]]];
  return (
    <>
      <div className="mad-funnel">
        {steps.map(([l, v], i) => (
          <div className="mad-funnel-step" key={l}>
            <span className="l">{l}</span>
            <span className="mad-funnel-track"><span style={{ width: `${base ? ((v || 0) / base) * 100 : 0}%`, background: PALETTE[i] }} /></span>
            <span className="v">{compact(v)}</span>
            <span className="p">{base ? `${(((v || 0) / base) * 100).toFixed(0)}%` : '—'}</span>
          </div>
        ))}
      </div>
      <div className="mis-stats mad-mini-stats">
        <Stat label="ThruPlays" value={compact(totals.thruplays)} sub="15 s o completo" />
        <Stat label="VTR" value={pct(totals.vtr)} sub="vistas completas / impresiones" />
      </div>
    </>
  );
}
